import { API_URL } from "../config/api";

export interface EscalationMatrixRecord {
  id: string;
  level_id: string;
  role_id: string;
  name: string;
  designation: string;
  email: string;
  phone: string;
  level: {
    id: string;
    name: string;
    order: number;
  };
  role: {
    id: string;
    name: string;
    description: string;
  };
}

export interface EscalationMatrixLevel {
  id: string;
  project_id: string;
  name: string;
  description: string;
  priority: number;
  response_time: number;
  escalation_threshold: number;
  notification_methods: string[];
}

export interface EscalationMatrixRole {
  id: string;
  name: string;
  description: string;
}

export interface EscalationMatrixResponse {
  total: number;
  page: number;
  size: number;
  results: EscalationMatrixRecord[];
}

export interface EscalationMatrixLevelsResponse {
  total: number;
  page: number;
  size: number;
  results: EscalationMatrixLevel[];
}

export interface EscalationMatrixRolesResponse {
  total: number;
  page: number;
  size: number;
  results: EscalationMatrixRole[];
}

/**
 * Get escalation matrix records for a project
 */
export const getEscalationMatrix = async (
  token: string,
  projectId: string,
  page: number = 1,
  size: number = 100
): Promise<EscalationMatrixResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    const response = await fetch(`${API_URL}/escalation-matrix/project/${projectId}?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: EscalationMatrixResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to fetch escalation matrix');
  }
};

/**
 * Create escalation matrix record
 */
export const createEscalationMatrixRecord = async (
  token: string,
  projectId: string,
  record: Omit<EscalationMatrixRecord, 'id' | 'level' | 'role'>
): Promise<EscalationMatrixRecord> => {
  try {
    const response = await fetch(`${API_URL}/escalation-matrix/project/${projectId}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(record)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: EscalationMatrixRecord = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to create escalation matrix record');
  }
};

/**
 * Update escalation matrix record
 */
export const updateEscalationMatrixRecord = async (
  token: string,
  recordId: string,
  record: Partial<Omit<EscalationMatrixRecord, 'id' | 'level' | 'role'>>
): Promise<EscalationMatrixRecord> => {
  try {
    const response = await fetch(`${API_URL}/escalation-matrix/${recordId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(record)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: EscalationMatrixRecord = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to update escalation matrix record');
  }
};

/**
 * Delete escalation matrix record
 */
export const deleteEscalationMatrixRecord = async (
  token: string,
  recordId: string
): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/escalation-matrix/${recordId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to delete escalation matrix record');
  }
};

/**
 * Get escalation matrix levels for a project
 */
export const getEscalationMatrixLevels = async (
  token: string,
  projectId: string
): Promise<EscalationMatrixLevel[]> => {
  try {
    const response = await fetch(`${API_URL}/escalation-matrix/levels/project/${projectId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: EscalationMatrixLevelsResponse = await response.json();
    return data.results || [];
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to fetch escalation matrix levels');
  }
};

/**
 * Create escalation matrix level for a project
 */
export const createEscalationMatrixLevel = async (
  token: string,
  projectId: string,
  level: Omit<EscalationMatrixLevel, 'id' | 'project_id'>
): Promise<EscalationMatrixLevel> => {
  try {
    // Prepare the payload according to API specification
    const payload = {
      id: crypto.randomUUID(), // Generate UUID for the new level
      project_id: projectId,
      ...level
    };

    console.log('Creating escalation level with payload:', payload);

    const response = await fetch(`${API_URL}/escalation-matrix/levels/project/${projectId}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage += ` - ${JSON.stringify(errorData.detail)}`;
        }
      } catch (e) {
        // Ignore JSON parsing errors for error response
      }
      throw new Error(errorMessage);
    }

    const data: EscalationMatrixLevel = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to create escalation matrix level');
  }
};

/**
 * Update escalation matrix level
 */
export const updateEscalationMatrixLevel = async (
  token: string,
  levelId: string,
  level: Partial<EscalationMatrixLevel>
): Promise<EscalationMatrixLevel> => {
  try {
    console.log('Updating escalation level:', levelId, level);

    const response = await fetch(`${API_URL}/escalation-matrix/levels/${levelId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(level)
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage += ` - ${JSON.stringify(errorData.detail)}`;
        }
      } catch (e) {
        // Ignore JSON parsing errors for error response
      }
      throw new Error(errorMessage);
    }

    // Check if there's content in response
    if (response.status === 204 || response.headers.get('content-length') === '0') {
              // If server returns 204 No Content, return updated data
      return level as EscalationMatrixLevel;
    }

    const data: EscalationMatrixLevel = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to update escalation matrix level');
  }
};

/**
 * Get escalation matrix roles
 */
export const getEscalationMatrixRoles = async (
  token: string
): Promise<EscalationMatrixRole[]> => {
  try {
    const response = await fetch(`${API_URL}/escalation-matrix/roles`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: EscalationMatrixRole[] = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to fetch escalation matrix roles');
  }
}; 